import type { CSSProperties } from 'react';
import { cn } from '@shared/lib/utils';

export const BRAND_NAME = 'Dockstash';
export const BRAND_TAGLINE = 'One-click, verified backups for any Docker project.';
export const BRAND_SOCIAL_HANDLE = '@dockstash';
// Public source repository (Apache-2.0). Single source for every GitHub link
// on the marketing surface + the Organization JSON-LD sameAs entry.
export const BRAND_REPO_URL = 'https://github.com/SelmiAbderrahim/dockstash';
export const BRAND_LICENSE_URL = `${BRAND_REPO_URL}/blob/master/LICENSE`;
export const BRAND_THEME_COLOR = '#111113';
export const BRAND_PRIMARY_COLOR = BRAND_THEME_COLOR;

export const BRAND_ASSETS = {
  favicon: '/favicon.svg',
  manifest: '/site.webmanifest',
  maskIcon: '/safari-pinned-tab.svg',
  mark: '/brand/dockstash-mark.svg',
  logo: '/brand/dockstash-logo.svg',
  appleTouchIcon: '/apple-touch-icon.png',
  favicon16: '/icons/favicon-16.png',
  favicon32: '/icons/favicon-32.png',
  icon192: '/icons/icon-192.png',
  icon512: '/icons/icon-512.png',
  maskableIcon512: '/icons/maskable-512.png',
  ogDefault: '/og/default.png',
  ogLogo: '/og/logo.png',
} as const;

/**
 * GitHub brand mark as an inline `currentColor` SVG: lucide ships no brand
 * glyphs and the design system forbids brand hues. Shared here so both the
 * auth OAuth buttons and the marketing surface use one copy
 * (mern-feature-modules: no cross-feature internal imports).
 */
export const GitHubMark = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12" />
  </svg>
);

interface BrandLogoProps {
  className?: string;
  markOnly?: boolean;
  style?: CSSProperties;
}

export const BrandLogo = ({ className, markOnly = false, style }: BrandLogoProps) => (
  <span
    className={cn(
      'brand-logo inline-flex items-center gap-2 leading-none whitespace-nowrap text-inherit no-underline',
      className,
    )}
    style={style}
    aria-label={markOnly ? BRAND_NAME : undefined}
    role={markOnly ? 'img' : undefined}
  >
    <svg
      className="brand-logo__mark size-8 shrink-0"
      viewBox="0 0 64 64"
      aria-hidden="true"
      focusable="false"
    >
      <rect width="64" height="64" rx="14" fill="currentColor" />
      <rect x="13" y="13" width="38" height="10" rx="3" fill="var(--background)" />
      <rect x="13" y="27" width="38" height="10" rx="3" fill="var(--muted)" />
      <rect x="13" y="41" width="38" height="10" rx="3" fill="var(--background)" />
      <path
        d="M20 18h19M20 32h15M20 46h13"
        stroke="currentColor"
        strokeOpacity="0.56"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
      <circle cx="49" cy="46.5" r="9.5" fill="var(--background)" />
      <path
        d="m44.5 46.7 3 3 6.8-7.2"
        fill="none"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
    {!markOnly && (
      <span className="brand-logo__text text-lg font-bold tracking-normal">
        {BRAND_NAME}
      </span>
    )}
  </span>
);
